/**
 * Machine processing log (F6, pilot): which run was done on which machine, by whom, with what.
 *   /process-runs        GET ?equipmentId&stepCode&status&cropId&identifiedBy&from&to&page&limit (process-runs:read)
 *                        rows of v_run_balance + machine, operator, cleanout person, lots in, batches out,
 *                        machine_identified_by (QR_SCAN | LIST), open exception count
 *   /process-runs/:id    GET — run, balance, inputs, outputs with sacks, exceptions, trace events
 * Raw SQL rows (snake_case); kg as decimal strings.
 */
import { useQuery } from '@tanstack/react-query'
import { getOne, getPage, usePageQuery, type PageParams } from './core'
import { arr, iso, kgToGrams, num, obj, str, strOrNull } from './mappers'

const KEY = ['console', 'process-runs'] as const

/** kg (decimal string, or a number inside json_build_object) → grams. */
const kgG = (v: unknown) => kgToGrams(v as string | number | null | undefined)

export const RUN_STATUSES = ['OPEN', 'RUNNING', 'CLOSED', 'BALANCED', 'EXCEPTION', 'CANCELLED'] as const
export type MachineIdentifiedBy = 'QR_SCAN' | 'LIST'

export interface PersonRef {
  id: string
  name: string
}
const person = (v: unknown): PersonRef | null => {
  if (!v) return null
  const o = obj(v)
  return { id: str(o.id), name: str(o.name) || str(o.username) }
}

export interface RunLotIn {
  unitId: string
  code: string
  farmerCode: string | null
  inG: number | null
}
export interface RunBatchOut {
  unitId: string
  code: string
  unitType: string
  stream: string | null
  status: string
  netG: number | null
}

export interface ProcessRunRow {
  id: string
  code: string
  status: string
  stream: string | null
  stepCode: string
  cropId: string | null
  cropName: string | null
  equipmentId: string | null
  equipmentCode: string | null
  equipmentName: string | null
  locationName: string | null
  operator: PersonRef | null
  cleanoutBy: PersonRef | null
  openedAt: string | null
  closedAt: string | null
  inputG: number | null
  flushG: number | null
  productG: number | null
  byproductG: number | null
  rejectG: number | null
  outputG: number | null
  residualPct: string | null
  identifiedBy: MachineIdentifiedBy | null
  machineLabel: string | null
  lotsIn: RunLotIn[]
  batchesOut: RunBatchOut[]
  openExceptions: number
}

const identified = (v: unknown): MachineIdentifiedBy | null =>
  v === 'QR_SCAN' || v === 'LIST' ? v : null

export const mapProcessRun = (r: Record<string, unknown>): ProcessRunRow => ({
  id: str(r.run_id ?? r.id),
  code: str(r.run_code ?? r.code),
  status: str(r.status),
  stream: strOrNull(r.stream),
  stepCode: str(r.step_code),
  cropId: strOrNull(r.crop_id),
  cropName: strOrNull(r.crop_name),
  equipmentId: strOrNull(r.equipment_id),
  equipmentCode: strOrNull(r.equipment_code),
  equipmentName: strOrNull(r.equipment_name),
  locationName: strOrNull(r.location_name),
  operator: person(r.operator),
  cleanoutBy: person(r.cleanout_by),
  openedAt: iso(r.opened_at ?? r.started_at),
  closedAt: iso(r.closed_at ?? r.ended_at),
  inputG: kgG(r.input_kg),
  flushG: kgG(r.flush_kg),
  productG: kgG(r.product_kg),
  byproductG: kgG(r.byproduct_kg),
  rejectG: kgG(r.reject_kg),
  outputG: kgG(r.output_kg),
  residualPct: strOrNull(r.residual_pct),
  identifiedBy: identified(r.machine_identified_by),
  machineLabel: strOrNull(r.equipment_qr_payload),
  lotsIn: arr(r.lots_in).map((l) => ({
    unitId: str(l.unit_id),
    code: str(l.code),
    farmerCode: strOrNull(l.farmer_code),
    inG: kgG(l.kg),
  })),
  batchesOut: arr(r.batches_out).map((b) => ({
    unitId: str(b.unit_id),
    code: str(b.code),
    unitType: str(b.unit_type),
    stream: strOrNull(b.stream),
    status: str(b.status),
    netG: kgG(b.net_kg),
  })),
  openExceptions: num(r.open_exceptions),
})

export interface ProcessRunFilters extends PageParams {
  equipmentId?: string
  stepCode?: string
  status?: string
  cropId?: string
  identifiedBy?: MachineIdentifiedBy
  /** IST dates, run opened on or after / on or before. */
  from?: string
  to?: string
}

export function useProcessRuns(f: ProcessRunFilters) {
  return usePageQuery([...KEY, 'list', f], () => getPage('/process-runs', f, mapProcessRun))
}

/** Every page of the filtered list (CSV export), up to `max` rows. */
export async function fetchAllProcessRuns(f: ProcessRunFilters, max = 2000): Promise<ProcessRunRow[]> {
  const out: ProcessRunRow[] = []
  for (let page = 1; out.length < max; page += 1) {
    const p = await getPage('/process-runs', { ...f, page, limit: 200 }, mapProcessRun)
    out.push(...p.rows)
    if (p.rows.length === 0 || page >= p.totalPages) break
  }
  return out
}

// ============================== detail ==============================

export interface RunInputRow {
  id: string
  unitId: string
  unitCode: string
  farmerCode: string | null
  tag: string | null
  weightG: number | null
  scannedAt: string | null
  scannedBy: PersonRef | null
  scannedById: string | null
}
export interface RunOutputRow {
  id: string
  code: string
  unitType: string
  streamKey: string | null
  status: string
  hold: string | null
  netG: number | null
  sacks: Array<{ tag: string | null; netG: number | null; status: string }>
}
export interface RunExceptionRow {
  id: string
  type: string
  severity: string
  status: string
  kinds: string[]
  raisedAt: string | null
}
export interface RunEventRow {
  id: string
  bizStep: string
  occurredAt: string | null
  actorId: string | null
  payload: Record<string, unknown>
}
export interface ProcessRunDetail {
  id: string
  code: string
  status: string
  stream: string | null
  stepCode: string
  cropName: string | null
  equipmentId: string | null
  equipmentCode: string | null
  equipmentName: string | null
  locationName: string | null
  operator: PersonRef | null
  cleanoutBy: PersonRef | null
  cleanoutAt: string | null
  openedAt: string | null
  closedAt: string | null
  originUnitId: string | null
  originUnitCode: string | null
  originFarmerCode: string | null
  identifiedBy: MachineIdentifiedBy | null
  machineLabel: string | null
  balance: {
    inputG: number | null
    flushG: number | null
    productG: number | null
    byproductG: number | null
    rejectG: number | null
    outputG: number | null
    residualG: number | null
    residualPct: string | null
    yieldPct: string | null
    lossLimitPct: string | null
    result: string | null
  }
  inputs: RunInputRow[]
  outputs: RunOutputRow[]
  exceptions: RunExceptionRow[]
  events: RunEventRow[]
}

export const mapProcessRunDetail = (r: Record<string, unknown>): ProcessRunDetail => {
  const b = obj(r.balance)
  const inputs = obj(r.inputs)
  return {
    id: str(r.id),
    code: str(r.code),
    status: str(r.status),
    stream: strOrNull(r.stream),
    stepCode: str(r.step_code),
    cropName: strOrNull(r.crop_name),
    equipmentId: strOrNull(r.equipment_id),
    equipmentCode: strOrNull(r.equipment_code),
    equipmentName: strOrNull(r.equipment_name),
    locationName: strOrNull(r.location_name),
    operator: person(r.operator),
    cleanoutBy: person(r.cleanout_by),
    cleanoutAt: iso(r.cleanout_confirmed_at),
    openedAt: iso(r.opened_at ?? r.started_at),
    closedAt: iso(r.ended_at),
    originUnitId: strOrNull(r.origin_unit_id),
    originUnitCode: strOrNull(r.origin_unit_code),
    originFarmerCode: strOrNull(r.origin_farmer_code),
    identifiedBy: identified(r.machine_identified_by),
    machineLabel: strOrNull(r.equipment_qr_payload),
    balance: {
      inputG: kgG(b.input_kg),
      flushG: kgG(b.flush_kg),
      productG: kgG(b.product_kg),
      byproductG: kgG(b.byproduct_kg),
      rejectG: kgG(b.reject_kg),
      outputG: kgG(b.output_kg),
      residualG: kgG(b.residual_kg),
      residualPct: strOrNull(b.residual_pct),
      yieldPct: strOrNull(b.yield_pct),
      lossLimitPct: strOrNull(b.loss_limit_pct),
      result: strOrNull(b.result),
    },
    inputs: arr(inputs.scanned_in).map((i) => ({
      id: str(i.id),
      unitId: str(i.unit_id),
      unitCode: str(i.unit_code),
      farmerCode: strOrNull(i.farmer_code),
      tag: strOrNull(i.tag),
      weightG: kgG(i.weight_kg),
      scannedAt: iso(i.scanned_at),
      scannedBy: person(i.scanned_by),
      scannedById: strOrNull(i.scanned_by_id),
    })),
    outputs: arr(r.outputs).map((o) => ({
      id: str(o.id),
      code: str(o.code),
      unitType: str(o.unit_type),
      streamKey: strOrNull(o.stream_key),
      status: str(o.status),
      hold: strOrNull(o.hold),
      netG: kgG(o.net_kg),
      sacks: arr(o.sacks).map((s) => ({
        tag: strOrNull(s.tag),
        netG: kgG(s.net_kg),
        status: str(s.status),
      })),
    })),
    exceptions: arr(r.exceptions).map((x) => {
      const d = obj(x.detail)
      const kinds = arr<unknown>(d.kinds).map(String)
      return {
        id: str(x.id),
        type: str(x.type),
        severity: str(x.severity),
        status: str(x.status),
        kinds: kinds.length ? kinds : d.kind ? [String(d.kind)] : [],
        raisedAt: iso(x.raised_at),
      }
    }),
    events: arr(r.events).map((e) => ({
      id: str(e.id),
      bizStep: str(e.biz_step),
      occurredAt: iso(e.occurred_at),
      actorId: strOrNull(e.actor_id),
      payload: obj(e.payload),
    })),
  }
}

export function useProcessRun(id: string | null) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/process-runs/${id}`, mapProcessRunDetail),
  })
}
